<template>
  <div class="sdash">
    <!-- ============ 인사 · 오늘 요약 ============ -->
    <header class="sdash__hero">
      <div class="sdash__hero-copy">
        <p class="sdash__hero-eyebrow">{{ todayLabel }}</p>
        <h2 class="sdash__hero-title">
          {{ storeName }} 판매 현황
        </h2>
        <p class="sdash__hero-desc">
          오늘 매출 {{ formatWon(todaySeries.revenue) }}원 · 주문 {{ todaySeries.orders }}건
          <template v-if="taskTotal"> · 처리할 일 {{ taskTotal }}건</template>
        </p>
      </div>
      <div class="sdash__hero-actions">
        <router-link to="/seller/orders?status=PAID" class="sui-btn sui-btn--primary">
          신규 주문
          <em v-if="countMapped('paid')">{{ countMapped('paid') }}</em>
        </router-link>
        <router-link to="/seller/orders?status=READY" class="sui-btn">
          발송 대기
          <em v-if="countMapped('ready')">{{ countMapped('ready') }}</em>
        </router-link>
        <router-link to="/seller/products?form=new" class="sui-btn">상품 등록</router-link>
      </div>
    </header>

    <!-- ============ 직접배송 강조 배너 ============ -->
    <aside
      class="sdash__notice"
      :class="{ 'is-urgent': directPendingCount > 0 }"
      data-tour="seller-dash-direct"
    >
      <span class="sdash__notice-icon" aria-hidden="true">
        <AppIcon icon="mdi-truck-delivery-outline" :size="22" />
      </span>
      <div class="sdash__notice-body">
        <p class="sdash__notice-title">직접배송은 「배송완료」를 직접 눌러 주세요</p>
        <p class="sdash__notice-text">
          택배 송장은 도착이 조회되면 자동으로 배송완료됩니다. 직접배송은 자동 조회가 없어,
          <strong>상품이 도착한 뒤 주문 관리에서 「배송완료」를 수동으로 눌러야</strong>
          정산에 반영됩니다.
        </p>
      </div>
      <router-link
        v-if="directPendingCount > 0"
        to="/seller/orders?status=SHIPPING"
        class="sdash__notice-cta"
      >
        대기 {{ directPendingCount }}건
        <AppIcon icon="mdi-chevron-right" :size="16" />
      </router-link>
      <router-link v-else to="/seller/orders?status=SHIPPING" class="sdash__notice-cta is-quiet">
        배송중 확인
        <AppIcon icon="mdi-chevron-right" :size="16" />
      </router-link>
    </aside>

    <!-- ============ KPI (3+3+3+3) ============ -->
    <article v-for="kpi in kpis" :key="kpi.label" class="sui-card sdash__kpi">
      <span class="sdash__kpi-icon" :class="`sdash__kpi-icon--${kpi.tone}`">
        <AppIcon :icon="kpi.icon" :size="20" />
      </span>
      <p class="sdash__kpi-label">{{ kpi.label }}</p>
      <p class="sdash__kpi-value">
        {{ kpi.value }}<em>{{ kpi.unit }}</em>
      </p>
      <p class="sdash__kpi-delta" :class="deltaClass(kpi.delta)">
        <AppIcon
          v-if="kpi.delta !== null"
          :icon="kpi.delta >= 0 ? 'mdi-trending-up' : 'mdi-trending-down'"
          :size="15"
        />
        {{ kpi.deltaText }}
      </p>
    </article>

    <!-- ============ 바로 처리할 일 (12) ============ -->
    <section class="sui-card sdash__todo" data-tour="seller-dash-tasks">
      <div class="sui-card__head">
        <h2 class="sui-card__title">
          바로 처리할 일 <em>{{ taskTotal }}건</em>
        </h2>
        <router-link to="/seller/orders" class="sui-card__link">
          주문 관리 <AppIcon icon="mdi-chevron-right" :size="15" />
        </router-link>
      </div>
      <div class="sui-card__body sdash__todo-body">
        <div class="sdash__todo-grid">
          <router-link
            v-for="task in tasks"
            :key="task.label"
            :to="task.to"
            class="sdash__task"
            :class="[`is-${task.tone}`, { 'is-hot': task.count > 0 && task.urgent }]"
          >
            <span class="sdash__task-icon">
              <AppIcon :icon="task.icon" :size="18" />
            </span>
            <span class="sdash__task-count" :class="{ 'is-zero': !task.count }">{{ task.count }}</span>
            <span class="sdash__task-label">{{ task.label }}</span>
            <span v-if="task.hint" class="sdash__task-hint">{{ task.hint }}</span>
          </router-link>
        </div>
      </div>
    </section>

    <!-- ============ 매출 추이 (8) ============ -->
    <section class="sui-card sdash__trend" data-tour="seller-dash-stats">
      <div class="sui-card__head">
        <h2 class="sui-card__title">
          매출 추이 <em>취소 · 반품 제외</em>
        </h2>
        <div class="sdash__range">
          <button
            v-for="option in RANGES"
            :key="option.days"
            type="button"
            class="sdash__range-btn"
            :class="{ 'is-active': range === option.days }"
            @click="range = option.days"
          >
            {{ option.label }}
          </button>
        </div>
      </div>

      <div class="sui-card__body sdash__trend-body">
        <div class="sdash__trend-sum">
          <div>
            <p class="sdash__sum-label">기간 매출</p>
            <p class="sdash__sum-value">{{ formatWon(rangeTotal.revenue) }}<em>원</em></p>
          </div>
          <div>
            <p class="sdash__sum-label">주문 건수</p>
            <p class="sdash__sum-value">{{ formatWon(rangeTotal.orders) }}<em>건</em></p>
          </div>
          <div>
            <p class="sdash__sum-label">건당 평균</p>
            <p class="sdash__sum-value">{{ formatWon(avgOrderValue) }}<em>원</em></p>
          </div>
          <div>
            <p class="sdash__sum-label">일 평균</p>
            <p class="sdash__sum-value">
              {{ formatWon(Math.round(rangeTotal.revenue / range)) }}<em>원</em>
            </p>
          </div>
        </div>

        <div class="sdash__trend-chart">
          <SellerTrendChart :points="trendPoints" aria-label="일별 매출 추이" />
        </div>
      </div>
    </section>

    <!-- ============ 주문 상태 (4) ============ -->
    <section class="sui-card sdash__status" data-tour="seller-dash-status">
      <div class="sui-card__head">
        <h2 class="sui-card__title">주문 상태 <em>최근 90일</em></h2>
        <router-link to="/seller/orders" class="sui-card__link">
          전체 <AppIcon icon="mdi-chevron-right" :size="15" />
        </router-link>
      </div>
      <div class="sui-card__body">
        <SellerDonutChart :slices="statusSlices" center-label="총 주문" aria-label="주문 상태 분포" />
      </div>
    </section>

    <!-- ============ 인기 상품 (4) ============ -->
    <section class="sui-card sdash__top" data-tour="seller-dash-top">
      <div class="sui-card__head">
        <h2 class="sui-card__title">잘 팔리는 상품 <em>최근 30일</em></h2>
        <router-link to="/seller/products" class="sui-card__link">
          상품 관리 <AppIcon icon="mdi-chevron-right" :size="15" />
        </router-link>
      </div>
      <div class="sui-card__body">
        <ol class="sdash__rank">
          <li v-for="(item, index) in topProducts" :key="item.code">
            <router-link :to="`/seller/products/${item.code}`" class="sdash__rank-link">
              <span class="sdash__rank-no">{{ index + 1 }}</span>
              <img
                v-if="item.image"
                class="sdash__rank-img"
                :src="item.image"
                :alt="item.name"
                loading="lazy"
              />
              <span class="sdash__rank-body">
                <span class="sdash__rank-name">{{ item.name }}</span>
                <span class="sdash__rank-bar">
                  <em :style="{ width: `${(item.revenue / topRevenue) * 100}%` }" />
                </span>
              </span>
              <span class="sdash__rank-num">
                <strong>{{ compactWon(item.revenue) }}원</strong>
                <em>{{ item.qty }}개</em>
              </span>
            </router-link>
          </li>
        </ol>
        <div v-if="!topProducts.length" class="sui-empty">
          <AppIcon icon="mdi-package-variant" :size="28" />
          <p>최근 30일 판매 데이터가 없습니다.</p>
        </div>
      </div>
    </section>

    <!-- ============ 재고 (4) ============ -->
    <section class="sui-card sdash__stock" data-tour="seller-dash-stock">
      <div class="sui-card__head">
        <h2 class="sui-card__title">재고 확인 <em>적은 순</em></h2>
        <router-link to="/seller/products?stock=low" class="sui-card__link">
          전체 재고 <AppIcon icon="mdi-chevron-right" :size="15" />
        </router-link>
      </div>
      <div class="sui-card__body">
        <ul v-if="stockWatch.length" class="sdash__stock-list">
          <li v-for="item in stockWatch" :key="item.code">
            <router-link
              class="sdash__stock-link"
              :to="`/seller/products?stock=low&code=${encodeURIComponent(item.code)}`"
            >
              <img
                v-if="item.thumbnail"
                class="sdash__stock-img"
                :src="item.thumbnail"
                :alt="item.name"
                loading="lazy"
              />
              <span class="sdash__stock-body">
                <span class="sdash__stock-name">{{ item.name }}</span>
                <span class="sdash__stock-sub">누적 판매 {{ item.soldCount }}개</span>
              </span>
              <span class="sui-badge" :class="stockBadgeClass(item.stock)">
                {{ item.stock === 0 ? '품절' : `${item.stock}개 남음` }}
              </span>
            </router-link>
          </li>
        </ul>
        <div v-else class="sui-empty sdash__stock-empty">
          <AppIcon icon="mdi-package-variant" :size="30" />
          <p>{{ productsLoading ? '불러오는 중…' : '등록된 상품이 없습니다.' }}</p>
        </div>
      </div>
    </section>

    <!-- ============ 정산 예정 (4) ============ -->
    <section class="sui-card sdash__payout" data-tour="seller-dash-payout">
      <div class="sui-card__head">
        <h2 class="sui-card__title">정산 예정</h2>
        <router-link to="/seller/settlements" class="sui-card__link">
          정산 관리 <AppIcon icon="mdi-chevron-right" :size="15" />
        </router-link>
      </div>
      <div class="sui-card__body">
        <p class="sdash__payout-amount">
          {{ formatWon(settlementPreview.payout) }}<em>원</em>
        </p>
        <p class="sdash__payout-date">결제 완료된 주문 기준 · 정산 예정액</p>
        <dl class="sdash__payout-detail">
          <div>
            <dt>판매 금액</dt>
            <dd>{{ formatWon(settlementPreview.salesAmount) }}원</dd>
          </div>
          <div>
            <dt>수수료</dt>
            <dd class="is-minus">-{{ formatWon(settlementPreview.platform) }}원</dd>
          </div>
          <div>
            <dt>판매업체 마진</dt>
            <dd>+{{ formatWon(settlementPreview.sellerMargin) }}원</dd>
          </div>
          <div>
            <dt>정산 예정 (원가+마진)</dt>
            <dd>{{ formatWon(settlementPreview.payout) }}원</dd>
          </div>
        </dl>
      </div>
    </section>

    <!-- ============ 최근 주문 (8) ============ -->
    <section class="sui-card sdash__orders" data-tour="seller-dash-orders">
      <div class="sui-card__head">
        <h2 class="sui-card__title">최근 주문</h2>
        <router-link to="/seller/orders" class="sui-card__link">
          전체 보기 <AppIcon icon="mdi-chevron-right" :size="15" />
        </router-link>
      </div>
      <div v-if="recentOrders.length" class="sui-table-wrap">
        <table class="sui-table">
          <thead>
            <tr>
              <th>주문번호 · 일시</th>
              <th>상품</th>
              <th class="is-num">금액</th>
              <th class="is-center">상태</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="order in recentOrders" :key="`${order.id}-${order.productName}`">
              <td>
                <span class="sui-table__strong">{{ order.id }}</span>
                <span class="sui-table__sub">{{ formatDateTime(order.orderedAt) }}</span>
              </td>
              <td>
                <span class="sui-prod">
                  <img
                    v-if="order.productImage"
                    class="sui-prod__img"
                    :src="order.productImage"
                    :alt="order.productName"
                    loading="lazy"
                  />
                  <span>
                    <span class="sui-prod__name">{{ order.productName }}</span>
                    <span class="sui-table__sub">
                      {{ order.buyerOrg }} · {{ order.qty }}개
                      <template v-if="order.directShip"> · 직접배송</template>
                    </span>
                  </span>
                </span>
              </td>
              <td class="is-num sui-table__strong">{{ formatWon(order.amount) }}원</td>
              <td class="is-center">
                <span class="sui-badge" :class="statusBadge(order.status)">
                  {{ SELLER_ORDER_STATUS_LABEL[order.status] }}
                </span>
                <span v-if="order.needsManualDone" class="sdash__manual-tag">수동 완료 필요</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="sui-empty">
        <AppIcon icon="mdi-cart-outline" :size="30" />
        <p>{{ loading ? '불러오는 중입니다…' : '결제 완료된 주문이 아직 없습니다.' }}</p>
      </div>
    </section>

    <!-- ============ 최근 리뷰 (12) ============ -->
    <section class="sui-card sdash__reviews" data-tour="seller-dash-reviews">
      <div class="sui-card__head">
        <h2 class="sui-card__title">
          최근 리뷰 <em>평균 {{ avgRating }}점 · 답변 대기 {{ unansweredReviews }}건</em>
        </h2>
        <router-link to="/seller/reviews?tab=review" class="sui-card__link">
          전체 보기 <AppIcon icon="mdi-chevron-right" :size="15" />
        </router-link>
      </div>
      <div class="sui-card__body">
        <ul v-if="recentReviews.length" class="sdash__review-list">
          <li v-for="review in recentReviews" :key="review.id">
            <router-link
              class="sdash__review"
              :to="`/seller/reviews?tab=review&productCode=${encodeURIComponent(review.productCode)}`"
            >
              <p class="sdash__review-top">
                <span class="sdash__stars" :aria-label="`${review.rating}점`">
                  <AppIcon
                    v-for="star in 5"
                    :key="star"
                    :icon="star <= review.rating ? 'mdi-star' : 'mdi-star-outline'"
                    :size="14"
                  />
                </span>
                <span class="sdash__review-date">{{ review.date }}</span>
              </p>
              <p class="sdash__review-text">{{ review.content }}</p>
              <p class="sdash__review-prod">{{ review.productName }}</p>
              <p class="sdash__review-foot">
                <span class="sdash__review-who">{{ review.author }} · {{ review.org }}</span>
                <span v-if="!review.reply" class="sdash__review-todo">
                  <AppIcon icon="mdi-reply" :size="13" />
                  답변 대기
                </span>
              </p>
            </router-link>
          </li>
        </ul>
        <div v-else class="sui-empty">
          <AppIcon icon="mdi-comment-outline" :size="28" />
          <p>아직 등록된 후기가 없습니다.</p>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import SellerTrendChart from '@/components/seller/SellerTrendChart.vue'
import SellerDonutChart from '@/components/seller/SellerDonutChart.vue'
import {
  LOW_STOCK_THRESHOLD,
  SELLER_ORDER_STATUS_COLOR,
  SELLER_ORDER_STATUS_FLOW,
  SELLER_ORDER_STATUS_LABEL,
  SELLER_ORDER_STATUS_TONE,
  compactWon,
  formatWon,
  type SellerOrderStatus,
} from '@/constants/sellerData'
import {
  fetchSellerOrders,
  type ItemStatusCounts,
  type MallOrderItemStatus,
  type SellerOrderItem,
} from '@/api/mallOrder'
import { fetchSellerQnas, fetchSellerReviews, type SellerMallReview } from '@/api/mallReview'
import { useSellerAuth } from '@/composables/useSellerAuth'
import { useSellerProducts } from '@/composables/useSellerProducts'

const { seller } = useSellerAuth()
const { products, loading: productsLoading, load: loadProducts } = useSellerProducts()

const storeName = computed(() => seller.value?.storeName || '판매업체')

const RANGES = [
  { days: 7, label: '7일' },
  { days: 30, label: '30일' },
  { days: 90, label: '90일' },
]

const range = ref(30)
const loading = ref(false)
const items = ref<SellerOrderItem[]>([])
const counts = ref<ItemStatusCounts>({})
const reviews = ref<SellerMallReview[]>([])
const openInquiryCount = ref(0)

const STATUS_MAP: Record<MallOrderItemStatus, SellerOrderStatus | null> = {
  WAITING: null,
  PAID: 'paid',
  READY: 'ready',
  SHIPPING: 'shipping',
  DELIVERED: 'delivered',
  DONE: 'delivered',
  CANCEL_REQUEST: 'cancelRequest',
  RETURN_REQUEST: 'returnRequest',
  CANCELED: 'cancelled',
}

function isDirectCourier(courier: string | undefined) {
  return /직접/.test(String(courier || ''))
}

const todayLabel = computed(() => {
  const now = new Date()
  const week = ['일', '월', '화', '수', '목', '금', '토'][now.getDay()]
  return `${now.getFullYear()}년 ${now.getMonth() + 1}월 ${now.getDate()}일 (${week})`
})

function dayKey(value: string | null | undefined): string {
  if (!value) return ''
  return String(value).slice(0, 10)
}

function addDays(base: Date, days: number): Date {
  const next = new Date(base)
  next.setDate(next.getDate() + days)
  return next
}

function toYmd(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function activeItems(list: SellerOrderItem[]) {
  return list.filter((item) => item.status !== 'CANCELED')
}

function buildSeries(days: number) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const start = addDays(today, -(days - 1))
  const map = new Map<string, { date: string; revenue: number; orders: number }>()

  for (let i = 0; i < days; i += 1) {
    const key = toYmd(addDays(start, i))
    map.set(key, { date: key, revenue: 0, orders: 0 })
  }

  activeItems(items.value).forEach((item) => {
    const key = dayKey(item.paidAt || item.orderedAt)
    const bucket = map.get(key)
    if (!bucket) return
    bucket.revenue += item.amount
    bucket.orders += 1
  })

  return [...map.values()]
}

function sumSeries(series: { revenue: number; orders: number }[]) {
  return series.reduce(
    (acc, point) => ({
      revenue: acc.revenue + point.revenue,
      orders: acc.orders + point.orders,
    }),
    { revenue: 0, orders: 0 },
  )
}

async function load() {
  loading.value = true
  try {
    const [orderResult, reviewList, qnaList] = await Promise.all([
      fetchSellerOrders(),
      fetchSellerReviews().catch(() => [] as SellerMallReview[]),
      fetchSellerQnas().catch(() => []),
    ])
    items.value = orderResult.items
    counts.value = orderResult.counts
    reviews.value = reviewList
    openInquiryCount.value = qnaList.filter((item) => !item.answer).length
  } catch {
    items.value = []
    counts.value = {}
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  load()
  loadProducts()
})

const rangeSeries = computed(() => buildSeries(range.value))
const rangeTotal = computed(() => sumSeries(rangeSeries.value))
const avgOrderValue = computed(() =>
  rangeTotal.value.orders ? Math.round(rangeTotal.value.revenue / rangeTotal.value.orders) : 0,
)

const trendPoints = computed(() =>
  rangeSeries.value.map((point) => ({
    label: `${Number(point.date.slice(5, 7))}.${point.date.slice(8, 10)}`,
    value: point.revenue,
  })),
)

const todaySeries = computed(() => buildSeries(1)[0] ?? { revenue: 0, orders: 0 })
const yesterdaySeries = computed(() => {
  const two = buildSeries(2)
  return two[0] ?? { revenue: 0, orders: 0 }
})
const last7 = computed(() => sumSeries(buildSeries(7)))
const prev7 = computed(() => {
  const fourteen = buildSeries(14)
  return sumSeries(fourteen.slice(0, 7))
})

function rate(current: number, previous: number): number | null {
  if (!previous) return null
  return Math.round(((current - previous) / previous) * 100)
}

function deltaText(value: number | null, suffix: string): string {
  if (value === null) return '비교 기준 없음'
  const sign = value >= 0 ? '+' : ''
  return `${sign}${value}% ${suffix}`
}

const settlementPreview = computed(() => {
  const list = activeItems(items.value)
  const salesAmount = list.reduce((sum, item) => sum + item.amount, 0)
  const platform = list.reduce((sum, item) => sum + item.platformMargin, 0)
  const sellerMargin = list.reduce((sum, item) => sum + item.sellerMargin, 0)
  const payout = list.reduce((sum, item) => sum + item.settlement, 0)
  return { salesAmount, platform, sellerMargin, payout }
})

const directPendingCount = computed(
  () =>
    items.value.filter((item) => item.status === 'SHIPPING' && isDirectCourier(item.courier)).length,
)

const kpis = computed(() => {
  const todayRate = rate(todaySeries.value.revenue, yesterdaySeries.value.revenue)
  const orderRate = rate(todaySeries.value.orders, yesterdaySeries.value.orders)
  const weekRate = rate(last7.value.revenue, prev7.value.revenue)

  return [
    {
      label: '오늘 매출',
      value: formatWon(todaySeries.value.revenue),
      unit: '원',
      icon: 'mdi-cash-multiple',
      tone: 'blue',
      delta: todayRate,
      deltaText: deltaText(todayRate, '어제 대비'),
    },
    {
      label: '오늘 주문',
      value: formatWon(todaySeries.value.orders),
      unit: '건',
      icon: 'mdi-cart-outline',
      tone: 'violet',
      delta: orderRate,
      deltaText: deltaText(orderRate, '어제 대비'),
    },
    {
      label: '최근 7일 매출',
      value: formatWon(last7.value.revenue),
      unit: '원',
      icon: 'mdi-chart-line',
      tone: 'green',
      delta: weekRate,
      deltaText: deltaText(weekRate, '직전 7일 대비'),
    },
    {
      label: '정산 예정 금액',
      value: formatWon(settlementPreview.value.payout),
      unit: '원',
      icon: 'mdi-bank-outline',
      tone: 'amber',
      delta: null,
      deltaText: '결제 완료 주문 기준',
    },
  ]
})

function deltaClass(delta: number | null) {
  if (delta === null) return 'is-flat'
  return delta >= 0 ? 'is-up' : 'is-down'
}

function countMapped(status: SellerOrderStatus): number {
  if (status === 'paid') return counts.value.PAID ?? 0
  if (status === 'ready') return counts.value.READY ?? 0
  if (status === 'shipping') return counts.value.SHIPPING ?? 0
  if (status === 'delivered') return (counts.value.DELIVERED ?? 0) + (counts.value.DONE ?? 0)
  if (status === 'cancelRequest') return counts.value.CANCEL_REQUEST ?? 0
  if (status === 'returnRequest') return counts.value.RETURN_REQUEST ?? 0
  if (status === 'cancelled') return counts.value.CANCELED ?? 0
  return 0
}

const unansweredReviews = computed(() => reviews.value.filter((review) => !review.reply).length)

const tasks = computed(() => [
  {
    label: '신규 주문',
    count: countMapped('paid'),
    to: '/seller/orders?status=PAID',
    icon: 'mdi-cart-arrow-down',
    tone: 'blue',
    urgent: true,
    hint: '주문 확인 · 발송',
  },
  {
    label: '발송 대기',
    count: countMapped('ready'),
    to: '/seller/orders?status=READY',
    icon: 'mdi-package-variant-closed',
    tone: 'violet',
    urgent: true,
    hint: '송장 입력',
  },
  {
    label: '직접배송 완료',
    count: directPendingCount.value,
    to: '/seller/orders?status=SHIPPING',
    icon: 'mdi-hand-back-right-outline',
    tone: 'amber',
    urgent: true,
    hint: '수동 「배송완료」',
  },
  {
    label: '취소 요청',
    count: countMapped('cancelRequest'),
    to: '/seller/claims',
    icon: 'mdi-close-circle-outline',
    tone: 'red',
    urgent: true,
    hint: '승인 · 반려',
  },
  {
    label: '반품 요청',
    count: countMapped('returnRequest'),
    to: '/seller/claims',
    icon: 'mdi-package-variant-remove',
    tone: 'red',
    urgent: true,
    hint: '승인 · 반려',
  },
  {
    label: '미답변 문의',
    count: openInquiryCount.value,
    to: '/seller/reviews?tab=inquiry',
    icon: 'mdi-message-question-outline',
    tone: 'green',
    urgent: false,
    hint: '고객 문의',
  },
  {
    label: '답변 대기 후기',
    count: unansweredReviews.value,
    to: '/seller/reviews',
    icon: 'mdi-star-outline',
    tone: 'green',
    urgent: false,
    hint: '후기 답글',
  },
])

const taskTotal = computed(() => tasks.value.reduce((sum, task) => sum + task.count, 0))

const statusSlices = computed(() =>
  SELLER_ORDER_STATUS_FLOW.map((status) => ({
    label: SELLER_ORDER_STATUS_LABEL[status],
    value: countMapped(status),
    color: SELLER_ORDER_STATUS_COLOR[status],
  })).filter((slice) => slice.value > 0),
)

function statusBadge(status: SellerOrderStatus): string {
  return SELLER_ORDER_STATUS_TONE[status]
}

const topProducts = computed(() => {
  const since = toYmd(addDays(new Date(), -29))
  const map = new Map<string, { code: string; name: string; image: string; revenue: number; qty: number }>()

  activeItems(items.value).forEach((item) => {
    const key = dayKey(item.paidAt || item.orderedAt)
    if (!key || key < since) return
    const code = item.productCode || item.name
    const row = map.get(code) ?? {
      code,
      name: item.name,
      image: item.image,
      revenue: 0,
      qty: 0,
    }
    row.revenue += item.amount
    row.qty += item.qty
    map.set(code, row)
  })

  return [...map.values()].sort((a, b) => b.revenue - a.revenue).slice(0, 5)
})

const topRevenue = computed(() => topProducts.value[0]?.revenue || 1)

const stockWatch = computed(() =>
  [...products.value].sort((a, b) => a.stock - b.stock || b.soldCount - a.soldCount).slice(0, 5),
)

function stockBadgeClass(stock: number): string {
  if (stock <= 0) return 'sui-badge--red'
  if (stock <= LOW_STOCK_THRESHOLD) return 'sui-badge--amber'
  return 'sui-badge--gray'
}

const recentOrders = computed(() =>
  items.value.slice(0, 6).map((item) => {
    const directShip = isDirectCourier(item.courier)
    return {
      id: item.orderCode,
      orderedAt: item.paidAt || item.orderedAt,
      productName: item.name,
      productImage: item.image,
      buyerOrg: item.buyerOrg,
      qty: item.qty,
      amount: item.amount,
      status: STATUS_MAP[item.status] || 'paid',
      directShip,
      needsManualDone: item.status === 'SHIPPING' && directShip,
    }
  }),
)

const recentReviews = computed(() => reviews.value.slice(0, 4))
const avgRating = computed(() => {
  if (!reviews.value.length) return '0.0'
  const sum = reviews.value.reduce((acc, review) => acc + review.rating, 0)
  return (sum / reviews.value.length).toFixed(1)
})

function formatDateTime(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return `${date.getMonth() + 1}.${date.getDate()} ${String(date.getHours()).padStart(2, '0')}:${String(
    date.getMinutes(),
  ).padStart(2, '0')}`
}
</script>

<style lang="scss" src="./SellerDashboardPage.scss"></style>
